/**
 * Catálogo do domínio meta `acesso` (T53, CAT-02).
 *
 * Origem: `src/shared/autorizacao/controle_acesso.ts`.
 *
 * É o domínio que administra o próprio catálogo de permissões
 * (`PADRONIZACAO-ROLES.md` §3.3), e por isso `GRUPOS.MKT` o exclui.
 */
import type { Concessao, NomePapel, Papel } from "../catalogo/vocabulario";
import type { RegraCompilada } from "../compilador";

/** `controle_acesso.ts:7` concede `read` em `Permissao` a qualquer autenticado, antes de papel. */
export const CONCESSOES_BASE_ACESSO: readonly Concessao[] = [
  { acoes: ["read"], recurso: "Permissao" },
];

export const PAPEIS_ACESSO = {
  /**
   * `manage` em `all`, e nao em `Permissao` — corrigido em T55, com a medicao
   * junto.
   *
   * A declaracao original concedia `manage` so em `Permissao`, e isso **tira
   * acesso**: `index.ts:44` concede `can("manage", "all")` a quem detem
   * `acesso.total`, o que alcanca os 30 recursos. Medido pelo teste de
   * equivalencia desta tarefa: **773 celulas da matriz acao x recurso** e cinco
   * decisoes por registro (`upload_dp@ProcessoJuridico`, `upload_seg@
   * ProcessoJuridico`, `delete@TicketInteracao` e duas de `Interagir@
   * TicketInteracao`) respondiam `true` no imperativo e `false` no catalogo.
   *
   * Ligar o catalogo com a declaracao antiga — que e o passo 4 da T56 —
   * arrancaria a administracao de quem a tem hoje, em silencio: nenhum teste do
   * dominio `acesso` olhava para fora de `Permissao`. E o dominio meta que
   * administra o catalogo inteiro (`PADRONIZACAO-ROLES.md` §3.3), entao o alcance
   * amplo e o ponto dele, nao um efeito colateral.
   *
   * O AD-026 diz a mesma coisa em portugues: "quem detem `acesso.total`
   * administra, e em que ambiente cada conta o detem passa a ser decisao de
   * operacao".
   */
  "acesso.total": {
    dominio: "acesso",
    nivel: "total",
    concessoes: [{ acoes: ["manage"], recurso: "all" }],
  },
} as const satisfies Partial<Record<NomePapel, Papel>>;

/**
 * Segunda concessão que não cabe em `Concessao`, e por um motivo diferente do de
 * `biblioteca`: aqui a condição não é sobre o registro, é sobre **o conjunto de
 * papéis do usuário**.
 *
 * `controle_acesso.ts:26` concede `editar` em `Permissao` a quem detém qualquer
 * papel de nível `admin`, em qualquer domínio (é a T47, que trocou o
 * `perm.includes("admin")` por checagem de nível pelo catálogo). Isso não é uma
 * concessão de um papel — é uma concessão derivada de ter algum. Nenhum
 * `PAPEIS_<D>.concessoes` pode expressá-la sem repeti-la nos 17 domínios.
 *
 * Fica aqui, no domínio meta, que é justamente o dono da pergunta "quem
 * administra permissão".
 */
export function concessoesCustomizadasDeAcesso(papeis: readonly string[]): RegraCompilada[] {
  const ehAdministrador = papeis.some((papel) => papel.split(".")[1] === "admin");
  return ehAdministrador ? [{ action: ["editar"], subject: "Permissao" }] : [];
}
